import { ContentType } from '@vivport/backend/types';
import type { CollectionKind, Content } from '@vivport/backend/types';
import type { LucideIcon } from 'lucide-react';
import {
  Anchor as AnchorIcon,
  Calendar,
  FileText,
  ImageIcon,
  Mic,
  Paperclip,
  Link,
  MessageSquare,
  Mail,
  GitBranch,
  Layers,
  LayoutTemplate,
  UserPen,
  Video as VideoIcon,
} from 'lucide-react';

export const CANONICAL_LABELS = {
  anchor: 'Anchor',
  event: 'Event',
  document: 'Document',
  recording: 'Recording',
  photo: 'Photo',
  video: 'Video',
  file: 'File',
  url: 'URL',
  neoChat: 'Neo Chat',
  message: 'Message',
  processRun: 'Run',
  manualSession: 'Manual Session',
  collection: 'Collection',
  template: 'Template',
  documentSection: 'Document section',
} as const;

// A collection's label comes from its KIND, never from ContentType.COLLECTION alone.
export const COLLECTION_KIND_LABELS: Record<CollectionKind, string> = {
  OBSERVATION: 'Observation',
  ALBUM: 'Album',
};

// The one place a collection's label is decided. A caller that does not know the kind
// gets the honest generic label rather than a guess at one of them.
export function getCollectionLabel(kind: CollectionKind | null | undefined): string {
  return kind ? COLLECTION_KIND_LABELS[kind] : CANONICAL_LABELS.collection;
}

// For callers holding the loaded Content. Content is discriminated on contentType, so
// the COLLECTION branch narrows `kind` to CollectionKind with no cast.
export function getContentDisplayLabel(content: Content): string {
  if (content.contentType === ContentType.COLLECTION) {
    return getCollectionLabel(content.kind);
  }
  return getContentTypeDisplayLabel(content.contentType);
}

export function pluralizeLabel(label: string): string {
  return label.endsWith('s') ? label : `${label}s`;
}

function normalizeContentTypeToken(contentType: ContentType | string | null | undefined): string {
  if (!contentType) {
    return '';
  }
  return String(contentType).trim().replace(/[-\s]+/g, '_').toUpperCase();
}

export function getContentTypeDisplayLabel(contentType: ContentType | string | null | undefined): string {
  const normalized = normalizeContentTypeToken(contentType);

  switch (normalized) {
    case 'ANCHOR':
      return CANONICAL_LABELS.anchor;
    case 'EVENT':
      return CANONICAL_LABELS.event;
    case 'DOCUMENT':
      return CANONICAL_LABELS.document;
    case 'PHOTO':
      return CANONICAL_LABELS.photo;
    case 'RECORDING':
      return CANONICAL_LABELS.recording;
    case 'VIDEO':
      return CANONICAL_LABELS.video;
    case 'FILE':
      return CANONICAL_LABELS.file;
    case 'URL':
      return CANONICAL_LABELS.url;
    case 'NEO_CHAT':
      return CANONICAL_LABELS.neoChat;
    case 'MESSAGE':
      return CANONICAL_LABELS.message;
    case 'PROCESS_RUN':
    case 'RUN':
      return CANONICAL_LABELS.processRun;
    case 'MANUAL_SESSION':
      return CANONICAL_LABELS.manualSession;
    case 'COLLECTION':
      return CANONICAL_LABELS.collection;
    case 'TEMPLATE':
      return CANONICAL_LABELS.template;
    case 'DOCUMENT_SECTION':
    case 'SECTION':
      return CANONICAL_LABELS.documentSection;
    default:
      return CANONICAL_LABELS.documentSection;
  }
}

export function getContentTypeIcon(
  contentType: ContentType | string | null | undefined,
): LucideIcon {
  const normalized = normalizeContentTypeToken(contentType);

  switch (normalized) {
    case 'ANCHOR':
      return AnchorIcon;
    case 'EVENT':
      return Calendar;
    case 'DOCUMENT':
      return FileText;
    case 'PHOTO':
      return ImageIcon;
    case 'RECORDING':
      return Mic;
    case 'VIDEO':
      return VideoIcon;
    case 'FILE':
      return Paperclip;
    case 'URL':
      return Link;
    case 'NEO_CHAT':
      return MessageSquare;
    case 'MESSAGE':
      return Mail;
    case 'PROCESS_RUN':
    case 'RUN':
      return GitBranch;
    case 'MANUAL_SESSION':
      return UserPen;
    case 'COLLECTION':
      return Layers;
    case 'TEMPLATE':
      return LayoutTemplate;
    default:
      return FileText;
  }
}

